/**
 * Bank transfers to match against the statement.
 *
 * A transfer at the till is believed on what the cashier saw and the
 * reference they typed. CenoX stops one reference paying twice; it cannot see
 * the bank. So each transfer and mobile-money payment waits here, "not
 * matched", until the owner or an admin finds it on the bank statement and
 * says so. Matching moves no money and changes no report figure.
 */

import { useState, type ReactNode } from 'react';
import { PAYMENT_METHOD_LABELS, type PaymentMethod } from '@cenox/shared';
import { useAuth } from '../../lib/auth';
import { dateTime, money } from '../../lib/format';
import { Badge, Button, Card, CardHeader, ErrorState, Input, Modal, Pagination, Spinner, cn } from '../../components/ui';
import { usePharmacy, usePharmacyWrite } from './data';
import { ErrorLine, Table, Td, Tile, errorText } from './parts';

interface Transfer {
  kind: 'sale' | 'old_debt';
  paymentId: string;
  against: string;
  customerName: string | null;
  amount: string;
  method: string;
  reference: string | null;
  paidAt: string;
  recordedBy: string | null;
  matchedAt: string | null;
  matchedBy: string | null;
  matchNote: string | null;
}

interface TransferList {
  data: Transfer[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
  unmatched: { count: number; amount: string };
}

const keyOf = (t: Pick<Transfer, 'kind' | 'paymentId'>) => `${t.kind}:${t.paymentId}`;

export function TransferMatchingSection(): ReactNode {
  const { business } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const tz = business?.timezone ?? 'Africa/Lagos';
  const [view, setView] = useState<'false' | 'true'>('false');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Map<string, Transfer>>(new Map());
  const [matching, setMatching] = useState(false);
  const [note, setNote] = useState('');
  const [undoing, setUndoing] = useState<Transfer | null>(null);
  const [reason, setReason] = useState('');
  const list = usePharmacy<TransferList>('/transfer-matching', { matched: view, page, pageSize: 50 });
  const match = usePharmacyWrite<{ items: Array<{ kind: string; paymentId: string }>; note: string }>('post', '/transfer-matching/match');
  const unmatch = usePharmacyWrite<{ kind: string; paymentId: string; reason: string }>('post', '/transfer-matching/unmatch');

  const toggle = (t: Transfer) =>
    setSelected((s) => {
      const next = new Map(s);
      if (next.has(keyOf(t))) next.delete(keyOf(t));
      else next.set(keyOf(t), t);
      return next;
    });

  if (list.isError) return <ErrorState message="Could not load transfers." onRetry={() => void list.refetch()} />;
  const d = list.data;

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader
          title="Bank transfers"
          subtitle="A reference typed at the till is what the cashier saw. It is not proof the money reached the bank — match each transfer against your bank statement."
        />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile label="Not yet matched" value={d ? d.unmatched.count : '—'} tone={d && d.unmatched.count ? 'warn' : 'plain'} />
          <Tile label="Amount not yet matched" value={d ? money(d.unmatched.amount, currency) : '—'} sub="transfers and mobile money" />
        </div>
      </Card>

      <Card padded={false}>
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 sm:p-5">
          <div role="tablist" aria-label="Matched or not" className="inline-flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
            {(
              [
                ['false', 'Not matched'],
                ['true', 'Matched'],
              ] as const
            ).map(([v, label]) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={view === v}
                onClick={() => {
                  setView(v);
                  setPage(1);
                  setSelected(new Map());
                }}
                className={cn('rounded-md px-3 py-1.5 text-sm font-medium', view === v ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-50')}
              >
                {label}
              </button>
            ))}
          </div>
          {view === 'false' && selected.size > 0 && (
            <Button onClick={() => setMatching(true)}>Found {selected.size} on the statement</Button>
          )}
        </div>
        {!d ? (
          <Spinner />
        ) : d.data.length === 0 ? (
          <p className="px-5 pb-5 text-sm text-slate-500">{view === 'false' ? 'Every transfer is matched.' : 'Nothing matched yet.'}</p>
        ) : (
          <Table head={[view === 'false' ? '' : 'Matched', 'When', 'For', 'Method', 'Reference typed', 'Amount', 'Taken by', '']} minWidth={860}>
            {d.data.map((t) => (
              <tr key={keyOf(t)}>
                <Td>
                  {view === 'false' ? (
                    <input
                      type="checkbox"
                      aria-label={`Select ${t.against} ${t.amount}`}
                      checked={selected.has(keyOf(t))}
                      onChange={() => toggle(t)}
                    />
                  ) : (
                    <span className="text-xs text-slate-600">
                      {dateTime(t.matchedAt, tz)} · {t.matchedBy ?? '—'}
                      {t.matchNote ? <span className="block text-slate-500">{t.matchNote}</span> : null}
                    </span>
                  )}
                </Td>
                <Td className="whitespace-nowrap">{dateTime(t.paidAt, tz)}</Td>
                <Td>
                  {t.against}
                  {t.customerName ? <span className="block text-xs text-slate-500">{t.customerName}</span> : null}
                </Td>
                <Td>{PAYMENT_METHOD_LABELS[t.method as PaymentMethod] ?? t.method}</Td>
                <Td className="font-mono text-xs">{t.reference ?? '—'}</Td>
                <Td className="tabular-nums">{money(t.amount, currency)}</Td>
                <Td>{t.recordedBy ?? '—'}</Td>
                <Td>
                  {view === 'true' && (
                    <Button size="sm" variant="ghost" onClick={() => setUndoing(t)}>
                      Undo
                    </Button>
                  )}
                  {view === 'false' && <Badge tone="warning">Not matched</Badge>}
                </Td>
              </tr>
            ))}
          </Table>
        )}
        {d && d.meta.total > d.meta.pageSize && (
          <div className="p-4">
            <Pagination page={d.meta.page} pageSize={d.meta.pageSize} total={d.meta.total} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <Modal open={matching} onClose={() => setMatching(false)} title={`Mark ${selected.size} transfer(s) as on the statement`}>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Only mark what you can see on the bank statement itself — not a screenshot or an alert. Say which statement.
          </p>
          <Input label="Statement" required value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. GTBank statement 1–7 Nov" />
          <ErrorLine error={match.error} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setMatching(false)}>Cancel</Button>
            <Button
              loading={match.isPending}
              disabled={note.trim().length < 3}
              onClick={() =>
                match.mutate(
                  { items: [...selected.values()].map((t) => ({ kind: t.kind, paymentId: t.paymentId })), note: note.trim() },
                  {
                    onSuccess: () => {
                      setMatching(false);
                      setSelected(new Map());
                      setNote('');
                    },
                  },
                )
              }
            >
              Mark as matched
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={undoing !== null} onClose={() => setUndoing(null)} title="Undo this match?">
        <div className="space-y-3">
          <Input label="Why" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Matched the wrong line" />
          {unmatch.error ? <p className="text-sm text-red-700">{errorText(unmatch.error)}</p> : null}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setUndoing(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={unmatch.isPending}
              disabled={reason.trim().length < 3}
              onClick={() =>
                undoing &&
                unmatch.mutate(
                  { kind: undoing.kind, paymentId: undoing.paymentId, reason: reason.trim() },
                  {
                    onSuccess: () => {
                      setUndoing(null);
                      setReason('');
                    },
                  },
                )
              }
            >
              Undo match
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
